import React from "react";
import { useMsal } from "@azure/msal-react";
import { Table, Form, Alert } from "react-bootstrap";
import { useBranch } from "../../../auth/BranchContext";
import "../../../assets/styles/informationLayer.scss";
import {
  resolveCommonBranchIdForFilters,
  scheduleAddressUpdate,
  updateAddress,
} from "../../../services/addressApi";

const InformationUpdateForms = ({
  addressData,
  selectedIds = [],
  changedRecords = [],
  addressFilterDto,
  searchArea,
  onRegisterActions,
}) => {
  const { instance, accounts } = useMsal();
  const activeAccount = instance.getActiveAccount() || accounts?.[0];
  const claims = activeAccount?.idTokenClaims || {};
  const rolesRaw = claims.roles || claims.role || claims.groups;
  const roles = Array.isArray(rolesRaw) ? rolesRaw : rolesRaw ? [rolesRaw] : [];
  const isSuperUser = roles.includes("ROLE_SUPERUSER");

  const dateFields = [
    {
      id: "executionDate",
      name: "executionDate",
      label: "Data wykonania:",
      type: "datetime-local",
    },
    {
      id: "withdrawalDate",
      name: "withdrawalDate",
      label: "Data wycofania:",
      type: "datetime-local",
    },
  ];

  const textFields = React.useMemo(
    () => [
      "EX_PN",
      "EX_WT",
      "EX_SR",
      "EX_CZ",
      "EX_PT",
      "EX_SO",
      "DR_PN",
      "DR_WT",
      "DR_SR",
      "DR_CZ",
      "DR_PT",
      "DR_SO",
      "W_PN",
      "W_WT",
      "W_SR",
      "W_CZ",
      "W_PT",
      "RES_PN_OD",
      "RES_PN_DO",
      "RES_WT_OD",
      "RES_WT_DO",
      "RES_SR_OD",
      "RES_SR_DO",
      "RES_CZ_OD",
      "RES_CZ_DO",
      "RES_PT_OD",
      "RES_PT_DO",
      "RES_SO_OD",
      "RES_SO_DO",
      "LastB_PN",
      "LastB_WT",
      "LastB_SR",
      "LastB_CZ",
      "LastB_PT",
      "LastB_SO",
      "STREFA",
    ],
    [],
  );
  const exDigitOnlyFields = React.useMemo(
    () => ["EX_PN", "EX_WT", "EX_SR", "EX_CZ", "EX_PT", "EX_SO"],
    [],
  );
  const drLetterOnlyFields = React.useMemo(
    () => ["DR_PN", "DR_WT", "DR_SR", "DR_CZ", "DR_PT", "DR_SO"],
    [],
  );
  const wLetterDigitFields = React.useMemo(
    () => ["W_PN", "W_WT", "W_SR", "W_CZ", "W_PT"],
    [],
  );
  const obszarOptions = ["", "M", "T"];
  const budynekDhlOptions = ["", "B", "C"];
  const boolOptions = [
    { value: "", label: "" },
    { value: "TRUE", label: "TAK" },
    { value: "FALSE", label: "NIE" },
  ];

  const toCamelCase = React.useCallback(
    (value) =>
      String(value)
        .toLowerCase()
        .split("_")
        .map((part, idx) =>
          idx === 0 ? part : part.charAt(0).toUpperCase() + part.slice(1),
        )
        .join(""),
    [],
  );

  const toPascalCase = React.useCallback(
    (value) => {
      const camel = toCamelCase(value);
      return camel ? camel.charAt(0).toUpperCase() + camel.slice(1) : "";
    },
    [toCamelCase],
  );

  const toDateTimeLocalInputValue = (value) => {
    if (value === undefined || value === null || value === "") return "";
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "";

    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    const hours = String(d.getHours()).padStart(2, "0");
    const minutes = String(d.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const extractTimeDigits = (value) => {
    if (value === undefined || value === null || value === "") return "";

    const asString = String(value).trim();
    const hhmmMatch = asString.match(/^(\d{1,2}):(\d{2})/);
    if (hhmmMatch) {
      return `${hhmmMatch[1].padStart(2, "0")}${hhmmMatch[2]}`;
    }

    const asDate = new Date(asString);
    if (!Number.isNaN(asDate.getTime())) {
      const hh = String(asDate.getHours()).padStart(2, "0");
      const mm = String(asDate.getMinutes()).padStart(2, "0");
      return `${hh}${mm}`;
    }

    const digits = asString.replace(/\D/g, "");
    return digits.slice(0, 4);
  };

  const normalizeBoolToOption = (value) => {
    if (value === true) return "TRUE";
    if (value === false) return "FALSE";
    const normalized = String(value ?? "")
      .trim()
      .toLowerCase();
    if (normalized === "true") return "TRUE";
    if (normalized === "false") return "FALSE";
    if (normalized === "tak") return "TRUE";
    if (normalized === "nie") return "FALSE";
    return String(value ?? "");
  };

  const getAddressFieldValue = React.useCallback(
    (...aliases) => {
      if (!addressData || typeof addressData !== "object") return undefined;

      for (const alias of aliases) {
        if (!alias) continue;
        const key = String(alias);
        if (Object.prototype.hasOwnProperty.call(addressData, key)) {
          return addressData[key];
        }
      }

      const normalizedMap = Object.entries(addressData).reduce(
        (acc, [k, v]) => {
          const normalizedKey = String(k).replace(/[_\s]/g, "").toLowerCase();
          if (!Object.prototype.hasOwnProperty.call(acc, normalizedKey)) {
            acc[normalizedKey] = v;
          }
          return acc;
        },
        {},
      );

      for (const alias of aliases) {
        if (!alias) continue;
        const normalizedAlias = String(alias)
          .replace(/[_\s]/g, "")
          .toLowerCase();
        if (
          Object.prototype.hasOwnProperty.call(normalizedMap, normalizedAlias)
        ) {
          return normalizedMap[normalizedAlias];
        }
      }

      return undefined;
    },
    [addressData],
  );

  const getRecordBranchId = React.useCallback((record) => {
    if (!record || typeof record !== "object") return undefined;

    const rawBranchId =
      record.branchId ??
      record.BranchId ??
      record.branchID ??
      record.BranchID ??
      record.branch_id ??
      record.terminalId ??
      record.TerminalId;

    const numericBranchId = Number(rawBranchId);
    return Number.isFinite(numericBranchId) && numericBranchId > 0
      ? numericBranchId
      : undefined;
  }, []);

  const resolveCommonBranchIdFromSelectedRecords = React.useCallback(() => {
    const numericSelectedIds = new Set(
      (Array.isArray(selectedIds) ? selectedIds : [])
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0),
    );

    const sourceRecords = Array.isArray(changedRecords) ? changedRecords : [];

    const editedRecords =
      sourceRecords.length > 0
        ? sourceRecords.filter((record) => {
            if (!record || record.__missing) return false;
            if (numericSelectedIds.size === 0) return true;
            const numericId = Number(record.id ?? record.Id);
            return (
              Number.isFinite(numericId) && numericSelectedIds.has(numericId)
            );
          })
        : addressData && typeof addressData === "object"
          ? [addressData]
          : [];

    if (editedRecords.length === 0) return undefined;

    const uniqueBranchIds = new Set();
    for (const record of editedRecords) {
      const recordBranchId = getRecordBranchId(record);
      if (!recordBranchId) return undefined;
      uniqueBranchIds.add(recordBranchId);
      if (uniqueBranchIds.size > 1) return undefined;
    }

    return uniqueBranchIds.size === 1
      ? Array.from(uniqueBranchIds)[0]
      : undefined;
  }, [selectedIds, changedRecords, addressData, getRecordBranchId]);

  const getDefaultValueForNamedField = React.useCallback(
    (name) => {
      const baseAliases = [
        name,
        name.toLowerCase(),
        toCamelCase(name),
        toPascalCase(name),
      ];

      if (name === "OBSZAR") {
        baseAliases.push("region", "Region");
      }
      if (name === "BUDYNEK_DHL") {
        baseAliases.push("buildingDhl", "BuildingDhl");
      }
      if (name === "STREFA") {
        baseAliases.push("zoneName", "ZoneName", "zone");
      }
      if (name === "executionDate") {
        baseAliases.push("executeAtLocal", "scheduledTime", "executionDate");
      }
      if (name === "withdrawalDate") {
        baseAliases.push(
          "withdrawAtLocal",
          "rollbackScheduledTime",
          "withdrawalDate",
        );
      }

      const value = getAddressFieldValue(...baseAliases);
      return value === undefined || value === null ? "" : String(value);
    },
    [getAddressFieldValue, toCamelCase, toPascalCase],
  );

  const initialExHistFromDigits = React.useMemo(
    () => extractTimeDigits(getDefaultValueForNamedField("EX_HIST_FROM")),
    [getDefaultValueForNamedField],
  );

  const initialExHistToDigits = React.useMemo(
    () => extractTimeDigits(getDefaultValueForNamedField("EX_HIST_TO")),
    [getDefaultValueForNamedField],
  );

  const initialDrHistFromDigits = React.useMemo(
    () => extractTimeDigits(getDefaultValueForNamedField("DR_HIST_FROM")),
    [getDefaultValueForNamedField],
  );

  const initialDrHistToDigits = React.useMemo(
    () => extractTimeDigits(getDefaultValueForNamedField("DR_HIST_TO")),
    [getDefaultValueForNamedField],
  );

  const initialUpdateTerminal = React.useMemo(() => {
    const raw = getAddressFieldValue(
      "UPDATE_TERMINAL",
      "updateTerminal",
      "terminalId",
      "branchId",
      "BranchId",
    );
    return raw === undefined || raw === null ? "" : String(raw);
  }, [getAddressFieldValue]);

  const initialExHistOption = React.useMemo(
    () => normalizeBoolToOption(getDefaultValueForNamedField("EX_HIST")),
    [getDefaultValueForNamedField],
  );

  const initialDrHistOption = React.useMemo(
    () => normalizeBoolToOption(getDefaultValueForNamedField("DR_HIST")),
    [getDefaultValueForNamedField],
  );

  const [exHistFromDigits, setExHistFromDigits] = React.useState("");
  const handleExHistFromChange = (e) => {
    const value = e.target.value;

    const digits = value.replace(/\D/g, "").slice(0, 4);
    setExHistFromDigits(digits);
  };

  const [exHistToDigits, setExHistToDigits] = React.useState("");
  const handleExHistToChange = (e) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 4);
    setExHistToDigits(digits);
  };

  const [drHistFromDigits, setDrHistFromDigits] = React.useState("");
  const handleDrHistFromChange = (e) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 4);
    setDrHistFromDigits(digits);
  };

  const [drHistToDigits, setDrHistToDigits] = React.useState("");
  const handleDrHistToChange = (e) => {
    const value = e.target.value;
    const digits = value.replace(/\D/g, "").slice(0, 4);
    setDrHistToDigits(digits);
  };

  const formatTimeDisplay = (digits) => {
    if (!digits) return "";
    if (digits.length <= 2) return digits;
    return digits.slice(0, 2) + ":" + digits.slice(2);
  };

  const isCompactTimeField = React.useCallback(
    (fieldName) => /^(RES_|LastB_)/.test(String(fieldName || "")),
    [],
  );

  const normalizeCompactTimeInput = React.useCallback((rawValue) => {
    const digits = String(rawValue ?? "")
      .replace(/\D/g, "")
      .slice(0, 4);
    return formatTimeDisplay(digits);
  }, []);

  const formRef = React.useRef(null);
  const [isSaving, setIsSaving] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [success, setSuccess] = React.useState(false);
  const [successMessage, setSuccessMessage] = React.useState(
    "Zlecenie zostało zapisane.",
  );
  const [updateTerminal, setUpdateTerminal] = React.useState("");
  const [exHistOption, setExHistOption] = React.useState("TRUE");
  const [drHistOption, setDrHistOption] = React.useState("TRUE");
  const [formEpoch, setFormEpoch] = React.useState(0);
  const [exFieldErrors, setExFieldErrors] = React.useState({});
  const [drFieldErrors, setDrFieldErrors] = React.useState({});
  const [wFieldErrors, setWFieldErrors] = React.useState({});
  const [strefaFieldErrors, setStrefaFieldErrors] = React.useState({});
  const [histTimeFieldErrors, setHistTimeFieldErrors] = React.useState({});
  const [compactTimeFieldErrors, setCompactTimeFieldErrors] = React.useState(
    {},
  );
  const [resRangeErrors, setResRangeErrors] = React.useState({});
  const [isSaveLocked, setIsSaveLocked] = React.useState(false);
  const [hasFormChanges, setHasFormChanges] = React.useState(false);
  const initialFormSnapshotRef = React.useRef("");

  const unlockSaveIfNeeded = React.useCallback(() => {
    setIsSaveLocked((prev) => (prev ? false : prev));
  }, []);

  const buildFormSnapshot = React.useCallback((formElement) => {
    if (!formElement) return "";

    const formData = new FormData(formElement);
    const entries = Array.from(formData.entries()).map(([key, val]) => [
      String(key),
      String(val ?? ""),
    ]);

    entries.sort(([keyA, valA], [keyB, valB]) => {
      if (keyA === keyB) return valA.localeCompare(valB);
      return keyA.localeCompare(keyB);
    });

    return JSON.stringify(entries);
  }, []);

  const evaluateFormChanges = React.useCallback(
    (formElement) => {
      if (!formElement) {
        setHasFormChanges(false);
        return;
      }

      const currentSnapshot = buildFormSnapshot(formElement);
      setHasFormChanges(currentSnapshot !== initialFormSnapshotRef.current);
    },
    [buildFormSnapshot],
  );

  const handleFormValueChange = React.useCallback(
    (event) => {
      unlockSaveIfNeeded();
      evaluateFormChanges(event.currentTarget);
    },
    [unlockSaveIfNeeded, evaluateFormChanges],
  );

  const normalizeValue = (value) => {
    if (value === undefined || value === null) return undefined;
    const trimmed = String(value).trim();
    return trimmed === "" ? undefined : trimmed;
  };

  const normalizeBoolPayloadValue = React.useCallback((value) => {
    if (value === undefined || value === null) return null;

    const normalized = String(value).trim();
    if (normalized === "") return null;

    const lower = normalized.toLowerCase();
    if (lower === "true" || lower === "tak") return true;
    if (lower === "false" || lower === "nie") return false;
    return null;
  }, []);

  const normalizeHistPayloadValue = (value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;

    const lower = String(normalized).trim().toLowerCase();
    if (lower === "true" || lower === "tak") return "TAK";
    if (lower === "false" || lower === "nie") return "NIE";

    return null;
  };

  const validateExFieldValue = React.useCallback((value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;
    if (!/^[1-9]\d{2}$/.test(normalized)) {
      return "Pole musi mieć dokładnie 3 cyfry i nie może zaczynać się od 0 (np. 123).";
    }
    return null;
  }, []);

  const validateAllExFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};
      exDigitOnlyFields.forEach((fieldName) => {
        const fieldError = validateExFieldValue(getValue(fieldName));
        if (fieldError) {
          nextErrors[fieldName] = fieldError;
        }
      });
      return nextErrors;
    },
    [exDigitOnlyFields, validateExFieldValue],
  );

  const validateDrFieldValue = React.useCallback((value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;
    if (!/^[A-Za-z]{2}$/.test(normalized)) {
      return "Pole musi mieć dokładnie 2 litery bez polskich znaków.";
    }
    const firstChar = normalized.charAt(0).toUpperCase();
    if (firstChar === "O" || firstChar === "I") {
      return "Pierwsza litera nie może być O ani I.";
    }
    return null;
  }, []);

  const validateWFieldValue = React.useCallback((value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;
    if (!/^[A-Za-z]\d{2}$/.test(normalized)) {
      return "Pole musi mieć dokładnie 1 literę i 2 cyfry (np. A12).";
    }
    const firstChar = normalized.charAt(0).toUpperCase();
    if (firstChar === "O" || firstChar === "I") {
      return "Litera nie może być O ani I.";
    }
    return null;
  }, []);

  const validateStrefaFieldValue = React.useCallback((value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;
    if (!/^[0-4]$/.test(normalized)) {
      return "Pole STREFA musi mieć wartość ze zbioru {0, 1, 2, 3, 4}.";
    }
    return null;
  }, []);

  const validateAllDrFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};
      drLetterOnlyFields.forEach((fieldName) => {
        const fieldError = validateDrFieldValue(getValue(fieldName));
        if (fieldError) {
          nextErrors[fieldName] = fieldError;
        }
      });
      return nextErrors;
    },
    [drLetterOnlyFields, validateDrFieldValue],
  );

  const validateAllWFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};
      wLetterDigitFields.forEach((fieldName) => {
        const fieldError = validateWFieldValue(getValue(fieldName));
        if (fieldError) {
          nextErrors[fieldName] = fieldError;
        }
      });
      return nextErrors;
    },
    [wLetterDigitFields, validateWFieldValue],
  );

  const validateAllStrefaFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};
      const fieldName = "STREFA";
      const fieldError = validateStrefaFieldValue(getValue(fieldName));
      if (fieldError) {
        nextErrors[fieldName] = fieldError;
      }
      return nextErrors;
    },
    [validateStrefaFieldValue],
  );

  const handleExFieldChange = React.useCallback(
    (fieldName, value) => {
      const fieldError = validateExFieldValue(value);
      setExFieldErrors((prev) => {
        const next = { ...prev };
        if (fieldError) {
          next[fieldName] = fieldError;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    [validateExFieldValue],
  );

  const handleDrFieldChange = React.useCallback(
    (fieldName, value) => {
      const fieldError = validateDrFieldValue(value);
      setDrFieldErrors((prev) => {
        const next = { ...prev };
        if (fieldError) {
          next[fieldName] = fieldError;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    [validateDrFieldValue],
  );

  const handleWFieldChange = React.useCallback(
    (fieldName, value) => {
      const fieldError = validateWFieldValue(value);
      setWFieldErrors((prev) => {
        const next = { ...prev };
        if (fieldError) {
          next[fieldName] = fieldError;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    [validateWFieldValue],
  );

  const handleStrefaFieldChange = React.useCallback(
    (fieldName, value) => {
      const fieldError = validateStrefaFieldValue(value);
      setStrefaFieldErrors((prev) => {
        const next = { ...prev };
        if (fieldError) {
          next[fieldName] = fieldError;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    [validateStrefaFieldValue],
  );

  const resRangePairs = React.useMemo(
    () => [
      ["RES_PN_OD", "RES_PN_DO"],
      ["RES_WT_OD", "RES_WT_DO"],
      ["RES_SR_OD", "RES_SR_DO"],
      ["RES_CZ_OD", "RES_CZ_DO"],
      ["RES_PT_OD", "RES_PT_DO"],
      ["RES_SO_OD", "RES_SO_DO"],
    ],
    [],
  );

  const resDoFields = React.useMemo(
    () => new Set(resRangePairs.map(([, doField]) => doField)),
    [resRangePairs],
  );

  const toMinutesFromCompactTime = React.useCallback((value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;

    const digits = String(normalized).replace(/\D/g, "");
    if (digits.length !== 4) return null;

    const hours = Number(digits.slice(0, 2));
    const minutes = Number(digits.slice(2, 4));

    if (
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return hours * 60 + minutes;
  }, []);

  const validateAllResRangeFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};

      resRangePairs.forEach(([odField, doField]) => {
        const odValue = getValue(odField);
        const doValue = getValue(doField);

        if (odValue === undefined || doValue === undefined) {
          return;
        }

        const odMinutes = toMinutesFromCompactTime(odValue);
        const doMinutes = toMinutesFromCompactTime(doValue);

        if (odMinutes === null || doMinutes === null) {
          return;
        }

        if (doMinutes <= odMinutes) {
          nextErrors[doField] =
            `Pole ${doField} musi być większe niż ${odField}.`;
        }
      });

      return nextErrors;
    },
    [resRangePairs, toMinutesFromCompactTime],
  );

  const validateCompactTimeFieldValue = React.useCallback(
    (value) => {
      const normalized = normalizeValue(value);
      if (normalized === undefined) return null;

      const digits = String(normalized).replace(/\D/g, "");
      if (digits.length !== 4) {
        return "Pole musi zawierać dokładnie 4 cyfry (HH:MM).";
      }

      if (toMinutesFromCompactTime(normalized) === null) {
        return "Pole musi zawierać poprawny czas (HH:MM).";
      }

      return null;
    },
    [toMinutesFromCompactTime],
  );

  const validateAllCompactTimeFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};

      textFields.forEach((fieldName) => {
        if (!isCompactTimeField(fieldName)) return;

        const fieldError = validateCompactTimeFieldValue(getValue(fieldName));
        if (fieldError) {
          nextErrors[fieldName] = fieldError;
        }
      });

      return nextErrors;
    },
    [isCompactTimeField, textFields, validateCompactTimeFieldValue],
  );

  const parseHistTimeToMinutes = React.useCallback((value) => {
    const normalized = normalizeValue(value);
    if (normalized === undefined) return null;

    const digits = String(normalized).replace(/\D/g, "");
    if (digits.length !== 4) return null;

    const hours = Number(digits.slice(0, 2));
    const minutes = Number(digits.slice(2, 4));

    if (
      !Number.isInteger(hours) ||
      !Number.isInteger(minutes) ||
      hours < 0 ||
      hours > 23 ||
      minutes < 0 ||
      minutes > 59
    ) {
      return null;
    }

    return hours * 60 + minutes;
  }, []);

  const validateHistTimeFieldValue = React.useCallback(
    (value, isHistEnabled) => {
      const normalized = normalizeValue(value);

      // If field is empty, it's only valid if history is NOT enabled
      if (normalized === undefined || normalized === "") {
        if (isHistEnabled) {
          return "Pole jest wymagane gdy odpowiedni HIST = TAK.";
        }
        return null;
      }

      // If field has content, validate the format
      const digits = String(normalized).replace(/\D/g, "");
      if (digits.length !== 4) {
        return "Pole musi zawierać dokładnie 4 cyfry (HH:MM).";
      }

      if (parseHistTimeToMinutes(normalized) === null) {
        return "Pole musi zawierać poprawny czas z zakresu 00:00-23:59.";
      }

      return null;
    },
    [parseHistTimeToMinutes],
  );

  const validateAllHistTimeFields = React.useCallback(
    (getValue) => {
      const nextErrors = {};
      const exHistEnabled =
        normalizeBoolPayloadValue(getValue("EX_HIST")) === true;
      const drHistEnabled =
        normalizeBoolPayloadValue(getValue("DR_HIST")) === true;

      const exFromError = validateHistTimeFieldValue(
        getValue("EX_HIST_FROM"),
        exHistEnabled,
      );
      if (exFromError) nextErrors.EX_HIST_FROM = exFromError;

      const exToError = validateHistTimeFieldValue(
        getValue("EX_HIST_TO"),
        exHistEnabled,
      );
      if (exToError) nextErrors.EX_HIST_TO = exToError;

      const drFromError = validateHistTimeFieldValue(
        getValue("DR_HIST_FROM"),
        drHistEnabled,
      );
      if (drFromError) nextErrors.DR_HIST_FROM = drFromError;

      const drToError = validateHistTimeFieldValue(
        getValue("DR_HIST_TO"),
        drHistEnabled,
      );
      if (drToError) nextErrors.DR_HIST_TO = drToError;

      return nextErrors;
    },
    [normalizeBoolPayloadValue, validateHistTimeFieldValue],
  );

  const handleHistTimeFieldChange = React.useCallback(
    (fieldName, value, isHistEnabled) => {
      const fieldError = validateHistTimeFieldValue(value, isHistEnabled);
      setHistTimeFieldErrors((prev) => {
        const next = { ...prev };
        if (fieldError) {
          next[fieldName] = fieldError;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    [validateHistTimeFieldValue],
  );

  const handleCompactTimeFieldChange = React.useCallback(
    (fieldName, value) => {
      const fieldError = validateCompactTimeFieldValue(value);
      setCompactTimeFieldErrors((prev) => {
        const next = { ...prev };
        if (fieldError) {
          next[fieldName] = fieldError;
        } else {
          delete next[fieldName];
        }
        return next;
      });
    },
    [validateCompactTimeFieldValue],
  );

  const refreshResRangeErrorsFromForm = React.useCallback(
    (formElement) => {
      if (!formElement) {
        setResRangeErrors({});
        return;
      }

      const formData = new FormData(formElement);
      const get = (key) => normalizeValue(formData.get(key));
      setResRangeErrors(validateAllResRangeFields(get));
    },
    [validateAllResRangeFields],
  );

  React.useEffect(() => {
    setExHistFromDigits(initialExHistFromDigits);
    setExHistToDigits(initialExHistToDigits);
    setDrHistFromDigits(initialDrHistFromDigits);
    setDrHistToDigits(initialDrHistToDigits);
    setUpdateTerminal(initialUpdateTerminal);
    setExHistOption(initialExHistOption);
    setDrHistOption(initialDrHistOption);
    setExFieldErrors({});
    setDrFieldErrors({});
    setWFieldErrors({});
    setStrefaFieldErrors({});
    setHistTimeFieldErrors({});
    setCompactTimeFieldErrors({});
    setResRangeErrors({});
    setHasFormChanges(false);
    setIsSaveLocked(false);
    setFormEpoch((prev) => prev + 1);
  }, [
    initialExHistFromDigits,
    initialExHistToDigits,
    initialDrHistFromDigits,
    initialDrHistToDigits,
    initialUpdateTerminal,
    initialExHistOption,
    initialDrHistOption,
  ]);

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const formElement = formRef.current;
      initialFormSnapshotRef.current = buildFormSnapshot(formElement);
      setHasFormChanges(false);
    }, 0);

    return () => clearTimeout(timer);
  }, [formEpoch, buildFormSnapshot]);

  const handleReset = () => {
    if (formRef.current) {
      formRef.current.reset();
    }
    setExHistFromDigits(initialExHistFromDigits);
    setExHistToDigits(initialExHistToDigits);
    setDrHistFromDigits(initialDrHistFromDigits);
    setDrHistToDigits(initialDrHistToDigits);
    setUpdateTerminal(initialUpdateTerminal);
    setExHistOption(initialExHistOption);
    setDrHistOption(initialDrHistOption);
    setExFieldErrors({});
    setDrFieldErrors({});
    setWFieldErrors({});
    setStrefaFieldErrors({});
    setHistTimeFieldErrors({});
    setCompactTimeFieldErrors({});
    setResRangeErrors({});
    setHasFormChanges(false);
    setIsSaveLocked(false);
    setError(null);
    setSuccess(false);
    setSuccessMessage("Zlecenie zostało zapisane.");

    setTimeout(() => {
      initialFormSnapshotRef.current = buildFormSnapshot(formRef.current);
      setHasFormChanges(false);
    }, 0);
  };

  const localDateTimeToIso = (localValue) => {
    const v = normalizeValue(localValue);
    if (!v) return null;
    const hasSeconds = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}$/.test(v);
    const isDateTimeLocal = /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}(:\d{2})?$/.test(v);
    if (!isDateTimeLocal) return null;
    return hasSeconds ? v : `${v}:00`;
  };

  const resolveIds = () => {
    if (Array.isArray(selectedIds) && selectedIds.length > 0) {
      return selectedIds
        .map((id) => Number(id))
        .filter((id) => Number.isFinite(id) && id > 0);
    }
    const singleId = addressData?.id ?? addressData?.Id ?? null;
    const n = Number(singleId);
    return Number.isFinite(n) && n > 0 ? [n] : undefined;
  };

  const normalizeDtoValue = (v) => {
    const n = normalizeValue(v);
    if (n === undefined) return null;
    const lower = String(n).toLowerCase();
    if (lower === "true") return true;
    if (lower === "false") return false;
    return n;
  };

  const normalizeSearchAreaPayload = React.useCallback((area) => {
    if (area === undefined || area === null || area === "") return null;
    if (typeof area === "object") return area;

    if (typeof area === "string") {
      const trimmed = area.trim();
      if (!trimmed) return null;
      try {
        return JSON.parse(trimmed.replace(/\\/g, ""));
      } catch {
        return null;
      }
    }

    return null;
  }, []);

  const buildAddressFilterDtoFromFiltersList = (filtersList) => {
    if (!Array.isArray(filtersList) || filtersList.length === 0) return null;

    const dto = {};
    for (const entry of filtersList) {
      if (!Array.isArray(entry) || entry.length < 1) continue;
      const rawKey = entry[0];
      const rawVal = entry[1];
      if (!rawKey) continue;
      dto[String(rawKey)] = normalizeDtoValue(rawVal);
    }

    const anyValue = Object.values(dto).some((v) => v !== null);
    return anyValue ? dto : null;
  };

  const shouldShowServiceAndSoFields = React.useMemo(() => {
    if (!Array.isArray(addressFilterDto) || addressFilterDto.length === 0) {
      return false;
    }

    const requiredFilterKeys = new Set([
      "postcode",
      "terminalex",
      "terminaldr",
    ]);

    return addressFilterDto.some((entry) => {
      const rawKey = Array.isArray(entry)
        ? entry[0]
        : (entry?.key ?? entry?.name ?? entry?.field ?? entry?.column);

      if (!rawKey) return false;

      const normalizedKey = String(rawKey).replace(/[_\s]/g, "").toLowerCase();

      return requiredFilterKeys.has(normalizedKey);
    });
  }, [addressFilterDto]);

  // Extract terminalEx / terminalDr values from applied filters
  const filterTerminalValues = React.useMemo(() => {
    if (!Array.isArray(addressFilterDto) || addressFilterDto.length === 0) {
      return { terminalEx: null, terminalDr: null };
    }

    let terminalEx = null;
    let terminalDr = null;

    for (const entry of addressFilterDto) {
      const rawKey = Array.isArray(entry)
        ? entry[0]
        : (entry?.key ?? entry?.name ?? entry?.field ?? entry?.column);
      const rawVal = Array.isArray(entry) ? entry[1] : entry?.value;

      if (!rawKey) continue;
      const normalizedKey = String(rawKey).replace(/[_\s]/g, "").toLowerCase();

      if (normalizedKey === "terminalex" && rawVal) {
        terminalEx = String(rawVal).trim();
      }
      if (normalizedKey === "terminaldr" && rawVal) {
        terminalDr = String(rawVal).trim();
      }
    }

    return { terminalEx, terminalDr };
  }, [addressFilterDto]);

  const API_RESPONSE_MESSAGE_MAPPINGS = React.useMemo(
    () => ({
      errors: [
        {
          status: 404,
          backendMessage:
            "No records found matching the criteria or user's permissions.",
          userMessage: "Nie znaleziono rekordów spełniających kryteria do których masz uprawnienia.",
        },
      ],
      successes: [
        {
          status: 200,
          backendMessage:
            "Only records for which the user has permission have been edited.",
          userMessage:
            "Uwaga! Zmiana nastąpi tylko dla rekordów do których masz uprawnienia.",
        },
        {
          status: 200,
          backendMessage:
            "Only records for which the user has permission will be edited on the scheduled date.",
          userMessage:
            "Uwaga! Zmiana nastąpi tylko dla rekordów do których masz uprawnienia.",
        },
        {
          status: 200,
          backendMessage:
            "Zmiana została przekazana do akceptacji przez power usera w zakładce Import danych.",
          userMessage:
            "Zmiana została przekazana do akceptacji przez power usera w zakładce Import danych.",
        },
      ],
    }),
    [],
  );

  const extractBackendMessage = React.useCallback((rawData, fallbackMessage = "") => {
    return String(
      (typeof rawData === "string" ? rawData : null) ??
        rawData?.message ??
        rawData?.error ??
        rawData?.title ??
        fallbackMessage ??
        "",
    ).trim();
  }, []);

  const resolveApiSuccessMessage = React.useCallback(
    (response, fallbackUserMessage) => {
      const status = response?.status;
      const normalizedBackendMessage = extractBackendMessage(response?.data);

      const mappedExactSuccess = API_RESPONSE_MESSAGE_MAPPINGS.successes.find(
        (entry) =>
          entry.status === status &&
          entry.backendMessage === normalizedBackendMessage,
      );

      return mappedExactSuccess?.userMessage ?? fallbackUserMessage;
    },
    [API_RESPONSE_MESSAGE_MAPPINGS, extractBackendMessage],
  );

  const resolveApiErrorMessage = (apiError) => {
    const status = apiError?.response?.status;
    const errorCode = apiError?.code;
    const normalizedBackendMessage = extractBackendMessage(
      apiError?.response?.data,
      apiError?.message,
    );

    const mappedExactError = API_RESPONSE_MESSAGE_MAPPINGS.errors.find(
      (entry) =>
        entry.status === status &&
        entry.backendMessage === normalizedBackendMessage,
    );

    if (mappedExactError) {
      return mappedExactError.userMessage;
    }

    const isTimeout =
      errorCode === "ECONNABORTED" ||
  /timeout|timed out/i.test(normalizedBackendMessage);

    if ((typeof status === "number" && status >= 500) || isTimeout) {
      return "Coś poszło nie tak.";
    }

    if (status === 423) {
      return "Baza danych jest obecnie zablokowana. Zmiana nie może zostać wykonana.";
    }

    if (
      status === 400 &&
      normalizedBackendMessage.includes(
        "ExecuteAtLocal cannot be later than WithdrawAtLocal.",
      )
    ) {
      return "Data zaplanowania nie może być późniejsza niż data wycofania.";
    }
  };

  const areValuesEqual = (a, b) => {
    if (a === b) return true;
    if (a == null && b == null) return true;
    return String(a) === String(b);
  };

  const getChangedPatchFields = (fieldEntries) => {
    const changed = {};

    for (const [payloadKey, currentValue, initialValue] of fieldEntries) {
      if (!areValuesEqual(currentValue, initialValue)) {
        changed[payloadKey] = currentValue;
      }
    }

    return changed;
  };

  const handleSave = async () => {
    if (!formRef.current || isSaving || isSaveLocked || !hasFormChanges) return;
    if (!isSuperUser && !normalizeValue(updateTerminal)) {
      setError(new Error("Pole terminal jest wymagane."));
      setSuccess(false);
      return;
    }

    // Validate terminal matches filter terminalEx / terminalDr
    if (!isSuperUser && normalizeValue(updateTerminal)) {
      const { terminalEx, terminalDr } = filterTerminalValues;

      if (terminalEx || terminalDr) {
        const expectedLabel = [terminalEx, terminalDr]
          .filter(Boolean)
          .join("\\");
        const selectedBranch = branches.find(
          (b) => String(b?.id ?? b?.branchId ?? b?.code ?? "") === updateTerminal,
        );
        const selectedName = String(
          selectedBranch?.name ?? selectedBranch?.code ?? updateTerminal,
        ).trim();

        const matchesEx = !terminalEx || selectedName.includes(terminalEx);
        const matchesDr = !terminalDr || selectedName.includes(terminalDr);

        if (!matchesEx || !matchesDr) {
          setError(
            new Error(
              `Wybrany terminal („${selectedName}") nie jest zgodny z filtrami (${expectedLabel}). Wybierz terminal pasujący do zastosowanych filtrów.`,
            ),
          );
          setSuccess(false);
          return;
        }
      }
    }

    const formData = new FormData(formRef.current);
    const get = (key) => normalizeValue(formData.get(key));

    const nextExErrors = validateAllExFields(get);
    const nextDrErrors = validateAllDrFields(get);
    const nextWErrors = validateAllWFields(get);
    const nextStrefaErrors = validateAllStrefaFields(get);
    const nextHistTimeErrors = validateAllHistTimeFields(get);
    const nextCompactTimeErrors = validateAllCompactTimeFields(get);
    const nextResRangeErrors = validateAllResRangeFields(get);
    setExFieldErrors(nextExErrors);
    setDrFieldErrors(nextDrErrors);
    setWFieldErrors(nextWErrors);
    setStrefaFieldErrors(nextStrefaErrors);
    setHistTimeFieldErrors(nextHistTimeErrors);
    setCompactTimeFieldErrors(nextCompactTimeErrors);
    setResRangeErrors(nextResRangeErrors);

    if (
      Object.keys(nextExErrors).length > 0 ||
      Object.keys(nextDrErrors).length > 0 ||
      Object.keys(nextWErrors).length > 0 ||
      Object.keys(nextStrefaErrors).length > 0 ||
      Object.keys(nextHistTimeErrors).length > 0 ||
      Object.keys(nextCompactTimeErrors).length > 0 ||
      Object.keys(nextResRangeErrors).length > 0
    ) {
      setError(
        new Error(
          "Pola EX_* / DR_* / W_* / STREFA / EX_HIST_* / DR_HIST_* / pola z maską HH:MM lub RES_*_DO są błędnie wypełnione.",
        ),
      );
      setSuccess(false);
      return;
    }

    // Conditional validation: DR_SO and EX_SO are required if SoN or SoO is true
    const soNIsTrue = normalizeBoolPayloadValue(get("SoN")) === true;
    const soOIsTrue = normalizeBoolPayloadValue(get("SoO")) === true;

    if (soNIsTrue || soOIsTrue) {
      const exSoValue = get("EX_SO");
      const drSoValue = get("DR_SO");

      if (!exSoValue) {
        nextExErrors["EX_SO"] =
          "Pole jest obowiązkowe, gdy SO_N lub SO_O = tak.";
      }
      if (!drSoValue) {
        nextDrErrors["DR_SO"] =
          "Pole jest obowiązkowe, gdy SO_N lub SO_O = tak.";
      }

      if (!exSoValue || !drSoValue) {
        setExFieldErrors(nextExErrors);
        setDrFieldErrors(nextDrErrors);
        setError(
          new Error(
            "Pola EX_SO i DR_SO są obowiązkowe, gdy SO_N lub SO_O = tak.",
          ),
        );
        setSuccess(false);
        return;
      }
    }

    const idsResolved = resolveIds();
    const filterDtoFromList =
      buildAddressFilterDtoFromFiltersList(addressFilterDto);
    const currentExHist = normalizeHistPayloadValue(get("EX_HIST"));
    const currentDrHist = normalizeHistPayloadValue(get("DR_HIST"));
    const normalizedSearchArea = normalizeSearchAreaPayload(searchArea);
    const canUseAddressFilterDto = !idsResolved || idsResolved.length === 0;
    const regionValue = shouldShowServiceAndSoFields ? get("OBSZAR") : undefined;
    const rawZoneValue = shouldShowServiceAndSoFields ? get("STREFA") : undefined;
    const zoneNameValue =
      rawZoneValue !== undefined ? Number(rawZoneValue) : undefined;

    const mergedAddressFilterDto = canUseAddressFilterDto
      ? {
          ...(filterDtoFromList && typeof filterDtoFromList === "object"
            ? filterDtoFromList
            : {}),
        }
      : null;

    const nextAddressFilterDto =
      mergedAddressFilterDto && Object.keys(mergedAddressFilterDto).length > 0
        ? mergedAddressFilterDto
        : null;

    const changeTargetDto = {
      ids: idsResolved && idsResolved.length > 0 ? idsResolved : null,
      addressFilterDto: nextAddressFilterDto,
      ...(normalizedSearchArea !== null
        ? { searchArea: normalizedSearchArea }
        : {}),
    };

    const payload = {
      executeAtLocal: localDateTimeToIso(get("executionDate")),
      withdrawAtLocal: localDateTimeToIso(get("withdrawalDate")),
      changeTargetDto,
      exPn: get("EX_PN"),
      exWt: get("EX_WT"),
      exSr: get("EX_SR"),
      exCz: get("EX_CZ"),
      exPt: get("EX_PT"),
      exSo: get("EX_SO"),
      drPn: get("DR_PN"),
      drWt: get("DR_WT"),
      drSr: get("DR_SR"),
      drCz: get("DR_CZ"),
      drPt: get("DR_PT"),
      drSo: get("DR_SO"),
      wPn: get("W_PN"),
      wWt: get("W_WT"),
      wSr: get("W_SR"),
      wCz: get("W_CZ"),
      wPt: get("W_PT"),
      resPnOd: get("RES_PN_OD"),
      resPnDo: get("RES_PN_DO"),
      resWtOd: get("RES_WT_OD"),
      resWtDo: get("RES_WT_DO"),
      resSrOd: get("RES_SR_OD"),
      resSrDo: get("RES_SR_DO"),
      resCzOd: get("RES_CZ_OD"),
      resCzDo: get("RES_CZ_DO"),
      resPtOd: get("RES_PT_OD"),
      resPtDo: get("RES_PT_DO"),
      resSoOd: get("RES_SO_OD"),
      resSoDo: get("RES_SO_DO"),
      lastBPn: get("LastB_PN"),
      lastBWt: get("LastB_WT"),
      lastBSr: get("LastB_SR"),
      lastBCz: get("LastB_CZ"),
      lastBPt: get("LastB_PT"),
      lastBSo: get("LastB_SO"),
      buildingDhl: get("BUDYNEK_DHL"),
      exHist: currentExHist,
      exHistFrom: currentExHist === "TAK" ? get("EX_HIST_FROM") : null,
      exHistTo: currentExHist === "TAK" ? get("EX_HIST_TO") : null,
      drHist: currentDrHist,
      drHistFrom: currentDrHist === "TAK" ? get("DR_HIST_FROM") : null,
      drHistTo: currentDrHist === "TAK" ? get("DR_HIST_TO") : null,
      service9: shouldShowServiceAndSoFields
        ? normalizeBoolPayloadValue(get("Service9"))
        : null,
      service12: shouldShowServiceAndSoFields
        ? normalizeBoolPayloadValue(get("Service12"))
        : null,
      service1722: shouldShowServiceAndSoFields
        ? normalizeBoolPayloadValue(get("Service17_22"))
        : null,
      soN: shouldShowServiceAndSoFields
        ? normalizeBoolPayloadValue(get("SoN"))
        : null,
      soO: shouldShowServiceAndSoFields
        ? normalizeBoolPayloadValue(get("SoO"))
        : null,
      updatedDate: new Date().toISOString(),
      ...(regionValue !== undefined ? { region: regionValue } : {}),
      ...(zoneNameValue !== undefined ? { zoneName: zoneNameValue } : {}),
    };

    setIsSaving(true);
    setError(null);
    setSuccess(false);

    const hasExplicitSelection =
      Array.isArray(idsResolved) && idsResolved.length > 0;

    const commonBranchIdFromSelectedRecords = hasExplicitSelection
      ? resolveCommonBranchIdFromSelectedRecords()
      : undefined;

    let commonBranchIdFromFilters;
    if (!hasExplicitSelection && filterDtoFromList) {
      try {
        commonBranchIdFromFilters = await resolveCommonBranchIdForFilters(
          filterDtoFromList,
        );
      } catch {
        commonBranchIdFromFilters = undefined;
      }
    }

    const numericBranchId = Number(updateTerminal);
    const manualBranchId =
      Number.isFinite(numericBranchId) && numericBranchId > 0
        ? numericBranchId
        : undefined;
    const branchId =
      commonBranchIdFromSelectedRecords ??
      commonBranchIdFromFilters ??
      (!isSuperUser ? manualBranchId : undefined);

    const hasScheduleDates = Boolean(
      payload.executeAtLocal || payload.withdrawAtLocal,
    );

    const initialExecuteAtLocal = localDateTimeToIso(
      getDefaultValueForNamedField("executionDate"),
    );
    const initialWithdrawAtLocal = localDateTimeToIso(
      getDefaultValueForNamedField("withdrawalDate"),
    );
    const initialZoneRaw = normalizeValue(
      getDefaultValueForNamedField("STREFA"),
    );
    const initialZoneName =
      initialZoneRaw === undefined ? null : Number(initialZoneRaw);
    const initialExHist = normalizeHistPayloadValue(
      getDefaultValueForNamedField("EX_HIST"),
    );
    const initialDrHist = normalizeHistPayloadValue(
      getDefaultValueForNamedField("DR_HIST"),
    );
    const initialExHistFrom =
      initialExHist === "TAK"
        ? normalizeValue(getDefaultValueForNamedField("EX_HIST_FROM"))
        : null;
    const initialExHistTo =
      initialExHist === "TAK"
        ? normalizeValue(getDefaultValueForNamedField("EX_HIST_TO"))
        : null;
    const initialDrHistFrom =
      initialDrHist === "TAK"
        ? normalizeValue(getDefaultValueForNamedField("DR_HIST_FROM"))
        : null;
    const initialDrHistTo =
      initialDrHist === "TAK"
        ? normalizeValue(getDefaultValueForNamedField("DR_HIST_TO"))
        : null;

    const patchFieldEntries = [
      ["executeAtLocal", payload.executeAtLocal, initialExecuteAtLocal],
      ["withdrawAtLocal", payload.withdrawAtLocal, initialWithdrawAtLocal],
      [
        "exPn",
        payload.exPn,
        normalizeValue(getDefaultValueForNamedField("EX_PN")),
      ],
      [
        "exWt",
        payload.exWt,
        normalizeValue(getDefaultValueForNamedField("EX_WT")),
      ],
      [
        "exSr",
        payload.exSr,
        normalizeValue(getDefaultValueForNamedField("EX_SR")),
      ],
      [
        "exCz",
        payload.exCz,
        normalizeValue(getDefaultValueForNamedField("EX_CZ")),
      ],
      [
        "exPt",
        payload.exPt,
        normalizeValue(getDefaultValueForNamedField("EX_PT")),
      ],
      [
        "exSo",
        payload.exSo,
        normalizeValue(getDefaultValueForNamedField("EX_SO")),
      ],
      [
        "drPn",
        payload.drPn,
        normalizeValue(getDefaultValueForNamedField("DR_PN")),
      ],
      [
        "drWt",
        payload.drWt,
        normalizeValue(getDefaultValueForNamedField("DR_WT")),
      ],
      [
        "drSr",
        payload.drSr,
        normalizeValue(getDefaultValueForNamedField("DR_SR")),
      ],
      [
        "drCz",
        payload.drCz,
        normalizeValue(getDefaultValueForNamedField("DR_CZ")),
      ],
      [
        "drPt",
        payload.drPt,
        normalizeValue(getDefaultValueForNamedField("DR_PT")),
      ],
      [
        "drSo",
        payload.drSo,
        normalizeValue(getDefaultValueForNamedField("DR_SO")),
      ],
      [
        "wPn",
        payload.wPn,
        normalizeValue(getDefaultValueForNamedField("W_PN")),
      ],
      [
        "wWt",
        payload.wWt,
        normalizeValue(getDefaultValueForNamedField("W_WT")),
      ],
      [
        "wSr",
        payload.wSr,
        normalizeValue(getDefaultValueForNamedField("W_SR")),
      ],
      [
        "wCz",
        payload.wCz,
        normalizeValue(getDefaultValueForNamedField("W_CZ")),
      ],
      [
        "wPt",
        payload.wPt,
        normalizeValue(getDefaultValueForNamedField("W_PT")),
      ],
      [
        "resPnOd",
        payload.resPnOd,
        normalizeValue(getDefaultValueForNamedField("RES_PN_OD")),
      ],
      [
        "resPnDo",
        payload.resPnDo,
        normalizeValue(getDefaultValueForNamedField("RES_PN_DO")),
      ],
      [
        "resWtOd",
        payload.resWtOd,
        normalizeValue(getDefaultValueForNamedField("RES_WT_OD")),
      ],
      [
        "resWtDo",
        payload.resWtDo,
        normalizeValue(getDefaultValueForNamedField("RES_WT_DO")),
      ],
      [
        "resSrOd",
        payload.resSrOd,
        normalizeValue(getDefaultValueForNamedField("RES_SR_OD")),
      ],
      [
        "resSrDo",
        payload.resSrDo,
        normalizeValue(getDefaultValueForNamedField("RES_SR_DO")),
      ],
      [
        "resCzOd",
        payload.resCzOd,
        normalizeValue(getDefaultValueForNamedField("RES_CZ_OD")),
      ],
      [
        "resCzDo",
        payload.resCzDo,
        normalizeValue(getDefaultValueForNamedField("RES_CZ_DO")),
      ],
      [
        "resPtOd",
        payload.resPtOd,
        normalizeValue(getDefaultValueForNamedField("RES_PT_OD")),
      ],
      [
        "resPtDo",
        payload.resPtDo,
        normalizeValue(getDefaultValueForNamedField("RES_PT_DO")),
      ],
      [
        "resSoOd",
        payload.resSoOd,
        normalizeValue(getDefaultValueForNamedField("RES_SO_OD")),
      ],
      [
        "resSoDo",
        payload.resSoDo,
        normalizeValue(getDefaultValueForNamedField("RES_SO_DO")),
      ],
      [
        "lastBPn",
        payload.lastBPn,
        normalizeValue(getDefaultValueForNamedField("LastB_PN")),
      ],
      [
        "lastBWt",
        payload.lastBWt,
        normalizeValue(getDefaultValueForNamedField("LastB_WT")),
      ],
      [
        "lastBSr",
        payload.lastBSr,
        normalizeValue(getDefaultValueForNamedField("LastB_SR")),
      ],
      [
        "lastBCz",
        payload.lastBCz,
        normalizeValue(getDefaultValueForNamedField("LastB_CZ")),
      ],
      [
        "lastBPt",
        payload.lastBPt,
        normalizeValue(getDefaultValueForNamedField("LastB_PT")),
      ],
      [
        "lastBSo",
        payload.lastBSo,
        normalizeValue(getDefaultValueForNamedField("LastB_SO")),
      ],
      [
        "buildingDhl",
        payload.buildingDhl,
        normalizeValue(getDefaultValueForNamedField("BUDYNEK_DHL")),
      ],
      ["exHist", payload.exHist, initialExHist],
      ["exHistFrom", payload.exHistFrom, initialExHistFrom],
      ["exHistTo", payload.exHistTo, initialExHistTo],
      ["drHist", payload.drHist, initialDrHist],
      ["drHistFrom", payload.drHistFrom, initialDrHistFrom],
      ["drHistTo", payload.drHistTo, initialDrHistTo],
    ];

    if (Object.prototype.hasOwnProperty.call(payload, "region")) {
      patchFieldEntries.push([
        "region",
        payload.region,
        normalizeValue(getDefaultValueForNamedField("OBSZAR")),
      ]);
    }

    if (Object.prototype.hasOwnProperty.call(payload, "zoneName")) {
      patchFieldEntries.push(["zoneName", payload.zoneName, initialZoneName]);
    }

    if (shouldShowServiceAndSoFields) {
      patchFieldEntries.push(
        [
          "service9",
          payload.service9,
          normalizeBoolPayloadValue(getDefaultValueForNamedField("Service9")),
        ],
        [
          "service12",
          payload.service12,
          normalizeBoolPayloadValue(getDefaultValueForNamedField("Service12")),
        ],
        [
          "service1722",
          payload.service1722,
          normalizeBoolPayloadValue(
            getDefaultValueForNamedField("Service17_22"),
          ),
        ],
        [
          "soN",
          payload.soN,
          normalizeBoolPayloadValue(getDefaultValueForNamedField("SoN")),
        ],
        [
          "soO",
          payload.soO,
          normalizeBoolPayloadValue(getDefaultValueForNamedField("SoO")),
        ],
      );
    }

    const patchChangedFields = getChangedPatchFields(patchFieldEntries);

    try {
      if (hasScheduleDates) {
        const schedulePayload = {
          ...patchChangedFields,
          changeTargetDto: payload.changeTargetDto,
          updatedDate: payload.updatedDate,
          ...(payload.executeAtLocal ? { executeAtLocal: payload.executeAtLocal } : {}),
          ...(payload.withdrawAtLocal ? { withdrawAtLocal: payload.withdrawAtLocal } : {}),
        };

        const response = await scheduleAddressUpdate(
          schedulePayload,
          undefined,
          branchId,
        );
        setIsSaveLocked(response?.status === 200);
        setSuccessMessage(
          resolveApiSuccessMessage(response, "Zlecenie zostało zapisane."),
        );
      } else {
        const patchPayload = {
          ...patchChangedFields,
          changeTargetDto: payload.changeTargetDto,
          updatedDate: payload.updatedDate,
        };

        if (Object.keys(patchChangedFields).length === 0) {
          setSuccessMessage("Brak zmian do zapisania.");
          setHasFormChanges(false);
          setSuccess(true);
          return;
        }

        const response = await updateAddress(patchPayload, undefined, branchId);
        const requiresAcceptance = response?.data?.requiresAcceptance === true;
        setIsSaveLocked(response?.status === 200);
        setSuccessMessage(
          resolveApiSuccessMessage(
            response,
            requiresAcceptance
              ? "Zmiana została przekazana do akceptacji przez power usera w zakładce Import danych."
              : "Zmiana wykonana.",
          ),
        );
      }
      initialFormSnapshotRef.current = buildFormSnapshot(formRef.current);
      setHasFormChanges(false);
      setSuccess(true);
    } catch (e) {
      setIsSaveLocked(false);
      const mappedMessage = resolveApiErrorMessage(e);
      setError(mappedMessage ? new Error(mappedMessage) : e);
    } finally {
      setIsSaving(false);
    }
  };

  const handleResetRef = React.useRef(handleReset);
  const handleSaveRef = React.useRef(handleSave);
  handleResetRef.current = handleReset;
  handleSaveRef.current = handleSave;

  React.useEffect(() => {
    if (!onRegisterActions) return undefined;

    onRegisterActions({
      onReset: () => handleResetRef.current(),
      onSave: () => handleSaveRef.current(),
      isSaving,
      isSaveLocked: isSaveLocked || !hasFormChanges,
    });

    return () => {
      onRegisterActions(null);
    };
  }, [onRegisterActions, isSaving, isSaveLocked, hasFormChanges]);

  const { branches = [] } = useBranch() || {};

  return (
    <Form ref={formRef} key={formEpoch} onChange={handleFormValueChange}>
      <div className="information-bulk-update-form-shell">
        <div className="information-bulk-update-card information-bulk-update-card--editor-form">
          <div className="information-bulk-update-card__body">
            <div className="information-bulk-update-form-intro">
              <span className="information-bulk-update-form-intro__label">
                Zakres danych do zmiany
              </span>
              <p className="mb-0 text-muted">
                Pozostaw puste pola bez zmian. Uzupełnij tylko te wartości,
                które mają zostać nadpisane.
              </p>
            </div>
            <Table className="layer-update-table" responsive>
              <tbody>
                {!isSuperUser && (
                  <tr>
                    <td>Terminal dla zmiany:</td>
                    <td>
                      <Form.Select
                        size="sm"
                        id="UPDATE_TERMINAL"
                        name="UPDATE_TERMINAL"
                        value={updateTerminal}
                        onChange={(event) =>
                          setUpdateTerminal(event.target.value)
                        }
                        required
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "16px",
                        }}
                      >
                        <option value="">--- Terminal ---</option>
                        {branches.map((b) => {
                          const value =
                            b?.id ?? b?.branchId ?? b?.code ?? b?.name ?? "";
                          const label = b?.name ?? b?.code ?? b?.id ?? value;
                          return (
                            <option key={String(value)} value={String(value)}>
                              {String(label)}
                            </option>
                          );
                        })}
                      </Form.Select>
                    </td>
                  </tr>
                )}
                {dateFields.map((f) => (
                  <tr key={f.id}>
                    <td>{f.label}</td>
                    <td>
                      <Form.Control
                        type={f.type}
                        size="sm"
                        id={f.id}
                        name={f.name ?? f.id}
                        defaultValue={toDateTimeLocalInputValue(
                          getDefaultValueForNamedField(f.name ?? f.id),
                        )}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "16px",
                        }}
                      />
                    </td>
                  </tr>
                ))}

                {textFields.map((name) => (
                  name === "STREFA" && !shouldShowServiceAndSoFields ? null : (
                  <tr key={name}>
                    <td>{name}:</td>
                    <td>
                      <Form.Control
                        type="text"
                        size="sm"
                        id={name}
                        name={name}
                        defaultValue={
                          isCompactTimeField(name)
                            ? normalizeCompactTimeInput(
                                getDefaultValueForNamedField(name),
                              )
                            : getDefaultValueForNamedField(name)
                        }
                        placeholder={
                          isCompactTimeField(name) ? "__:__" : undefined
                        }
                        inputMode={
                          exDigitOnlyFields.includes(name) ||
                          isCompactTimeField(name) ||
                          name === "STREFA"
                            ? "numeric"
                            : undefined
                        }
                        pattern={
                          exDigitOnlyFields.includes(name)
                            ? "[1-9]\\d{2}"
                            : drLetterOnlyFields.includes(name)
                              ? "[A-Za-z]{2}"
                              : wLetterDigitFields.includes(name)
                                ? "[A-Za-z]\\d{2}"
                                : name === "STREFA"
                                  ? "[0-4]"
                                  : isCompactTimeField(name)
                                    ? "[0-9:]*"
                                    : undefined
                        }
                        maxLength={
                          exDigitOnlyFields.includes(name)
                            ? 3
                            : drLetterOnlyFields.includes(name)
                              ? 2
                              : wLetterDigitFields.includes(name)
                                ? 3
                                : name === "STREFA"
                                  ? 1
                                  : isCompactTimeField(name)
                                    ? 5
                                    : undefined
                        }
                        isInvalid={Boolean(
                          exFieldErrors[name] ||
                          drFieldErrors[name] ||
                          wFieldErrors[name] ||
                          strefaFieldErrors[name] ||
                          compactTimeFieldErrors[name] ||
                          resRangeErrors[name],
                        )}
                        onChange={(e) => {
                          if (isCompactTimeField(name)) {
                            e.target.value = normalizeCompactTimeInput(
                              e.target.value,
                            );
                            handleCompactTimeFieldChange(name, e.target.value);
                            refreshResRangeErrorsFromForm(e.currentTarget.form);
                          }

                          if (exDigitOnlyFields.includes(name)) {
                            handleExFieldChange(name, e.target.value);
                            return;
                          }

                          if (drLetterOnlyFields.includes(name)) {
                            const upperValue = String(
                              e.target.value ?? "",
                            ).toUpperCase();
                            e.target.value = upperValue;
                            handleDrFieldChange(name, upperValue);
                            return;
                          }

                          if (wLetterDigitFields.includes(name)) {
                            handleWFieldChange(name, e.target.value);
                            return;
                          }

                          if (name === "STREFA") {
                            handleStrefaFieldChange(name, e.target.value);
                          }
                        }}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "16px",
                        }}
                      />
                      {exDigitOnlyFields.includes(name) && (
                        <Form.Control.Feedback type="invalid">
                          {exFieldErrors[name]}
                        </Form.Control.Feedback>
                      )}
                      {drLetterOnlyFields.includes(name) && (
                        <Form.Control.Feedback type="invalid">
                          {drFieldErrors[name]}
                        </Form.Control.Feedback>
                      )}
                      {wLetterDigitFields.includes(name) && (
                        <Form.Control.Feedback type="invalid">
                          {wFieldErrors[name]}
                        </Form.Control.Feedback>
                      )}
                      {name === "STREFA" && (
                        <Form.Control.Feedback type="invalid">
                          {strefaFieldErrors[name]}
                        </Form.Control.Feedback>
                      )}
                      {isCompactTimeField(name) &&
                        compactTimeFieldErrors[name] && (
                          <Form.Control.Feedback type="invalid">
                            {compactTimeFieldErrors[name]}
                          </Form.Control.Feedback>
                        )}
                      {resDoFields.has(name) && (
                        <Form.Control.Feedback type="invalid">
                          {resRangeErrors[name]}
                        </Form.Control.Feedback>
                      )}
                    </td>
                  </tr>
                  )
                ))}

                {shouldShowServiceAndSoFields && (
                  <tr>
                    <td>OBSZAR:</td>
                    <td>
                      <Form.Select
                        size="sm"
                        id="OBSZAR"
                        name="OBSZAR"
                        defaultValue={getDefaultValueForNamedField("OBSZAR")}
                        style={{
                          border: "1px solid #d1d5db",
                          borderRadius: "6px",
                          padding: "8px 12px",
                          fontSize: "16px",
                        }}
                      >
                        {obszarOptions.map((o) => (
                          <option key={o} value={o}>
                            {o}
                          </option>
                        ))}
                      </Form.Select>
                    </td>
                  </tr>
                )}

                <tr>
                  <td>BUDYNEK_DHL:</td>
                  <td>
                    <Form.Select
                      size="sm"
                      id="BUDYNEK_DHL"
                      name="BUDYNEK_DHL"
                      defaultValue={getDefaultValueForNamedField("BUDYNEK_DHL")}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    >
                      {budynekDhlOptions.map((o) => (
                        <option key={o} value={o}>
                          {o}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                </tr>

                <tr>
                  <td>EX_HIST:</td>
                  <td>
                    <Form.Select
                      size="sm"
                      id="EX_HIST"
                      name="EX_HIST"
                      value={exHistOption}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        const isEnabled = nextValue === "TRUE";
                        setExHistOption(nextValue);
                        if (!isEnabled) {
                          setExHistFromDigits("");
                          setExHistToDigits("");
                        }
                        setHistTimeFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.EX_HIST_FROM;
                          delete next.EX_HIST_TO;
                          return next;
                        });
                      }}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    >
                      {boolOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                </tr>

                <tr>
                  <td>EX_HIST_FROM:</td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      id="EX_HIST_FROM"
                      name="EX_HIST_FROM"
                      value={formatTimeDisplay(exHistFromDigits)}
                      placeholder="HH:MM"
                      disabled={exHistOption !== "TRUE"}
                      isInvalid={Boolean(histTimeFieldErrors.EX_HIST_FROM)}
                      onChange={(e) => {
                        handleExHistFromChange(e);
                        handleHistTimeFieldChange(
                          "EX_HIST_FROM",
                          e.target.value,
                          exHistOption === "TRUE",
                        );
                      }}
                      maxLength={5}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {histTimeFieldErrors.EX_HIST_FROM}
                    </Form.Control.Feedback>
                  </td>
                </tr>

                <tr>
                  <td>EX_HIST_TO:</td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      id="EX_HIST_TO"
                      name="EX_HIST_TO"
                      value={formatTimeDisplay(exHistToDigits)}
                      placeholder="HH:MM"
                      disabled={exHistOption !== "TRUE"}
                      isInvalid={Boolean(histTimeFieldErrors.EX_HIST_TO)}
                      onChange={(e) => {
                        handleExHistToChange(e);
                        handleHistTimeFieldChange(
                          "EX_HIST_TO",
                          e.target.value,
                          exHistOption === "TRUE",
                        );
                      }}
                      maxLength={5}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {histTimeFieldErrors.EX_HIST_TO}
                    </Form.Control.Feedback>
                  </td>
                </tr>

                <tr>
                  <td>DR_HIST:</td>
                  <td>
                    <Form.Select
                      size="sm"
                      id="DR_HIST"
                      name="DR_HIST"
                      value={drHistOption}
                      onChange={(event) => {
                        const nextValue = event.target.value;
                        const isEnabled = nextValue === "TRUE";
                        setDrHistOption(nextValue);
                        if (!isEnabled) {
                          setDrHistFromDigits("");
                          setDrHistToDigits("");
                        }
                        setHistTimeFieldErrors((prev) => {
                          const next = { ...prev };
                          delete next.DR_HIST_FROM;
                          delete next.DR_HIST_TO;
                          return next;
                        });
                      }}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    >
                      {boolOptions.map((o) => (
                        <option key={o.value} value={o.value}>
                          {o.label}
                        </option>
                      ))}
                    </Form.Select>
                  </td>
                </tr>

                <tr>
                  <td>DR_HIST_FROM:</td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      id="DR_HIST_FROM"
                      name="DR_HIST_FROM"
                      value={formatTimeDisplay(drHistFromDigits)}
                      placeholder="HH:MM"
                      disabled={drHistOption !== "TRUE"}
                      isInvalid={Boolean(histTimeFieldErrors.DR_HIST_FROM)}
                      onChange={(e) => {
                        handleDrHistFromChange(e);
                        handleHistTimeFieldChange(
                          "DR_HIST_FROM",
                          e.target.value,
                          drHistOption === "TRUE",
                        );
                      }}
                      maxLength={5}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {histTimeFieldErrors.DR_HIST_FROM}
                    </Form.Control.Feedback>
                  </td>
                </tr>

                <tr>
                  <td>DR_HIST_TO:</td>
                  <td>
                    <Form.Control
                      type="text"
                      size="sm"
                      id="DR_HIST_TO"
                      name="DR_HIST_TO"
                      value={formatTimeDisplay(drHistToDigits)}
                      placeholder="HH:MM"
                      disabled={drHistOption !== "TRUE"}
                      isInvalid={Boolean(histTimeFieldErrors.DR_HIST_TO)}
                      onChange={(e) => {
                        handleDrHistToChange(e);
                        handleHistTimeFieldChange(
                          "DR_HIST_TO",
                          e.target.value,
                          drHistOption === "TRUE",
                        );
                      }}
                      maxLength={5}
                      style={{
                        border: "1px solid #d1d5db",
                        borderRadius: "6px",
                        padding: "8px 12px",
                        fontSize: "16px",
                      }}
                    />
                    <Form.Control.Feedback type="invalid">
                      {histTimeFieldErrors.DR_HIST_TO}
                    </Form.Control.Feedback>
                  </td>
                </tr>

                {shouldShowServiceAndSoFields && (
                  <>
                    <tr>
                      <td>9</td>
                      <td>
                        <Form.Select
                          size="sm"
                          id="Service9"
                          name="Service9"
                          defaultValue={normalizeBoolToOption(
                            getDefaultValueForNamedField("Service9"),
                          )}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "16px",
                          }}
                        >
                          {boolOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                    <tr>
                      <td>12</td>
                      <td>
                        <Form.Select
                          size="sm"
                          id="Service12"
                          name="Service12"
                          defaultValue={normalizeBoolToOption(
                            getDefaultValueForNamedField("Service12"),
                          )}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "16px",
                          }}
                        >
                          {boolOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                    <tr>
                      <td>17_22</td>
                      <td>
                        <Form.Select
                          size="sm"
                          id="Service17_22"
                          name="Service17_22"
                          defaultValue={normalizeBoolToOption(
                            getDefaultValueForNamedField("Service17_22"),
                          )}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "16px",
                          }}
                        >
                          {boolOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                    <tr>
                      <td>SO_N</td>
                      <td>
                        <Form.Select
                          size="sm"
                          id="SoN"
                          name="SoN"
                          defaultValue={normalizeBoolToOption(
                            getDefaultValueForNamedField("SoN"),
                          )}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "16px",
                          }}
                        >
                          {boolOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                    <tr>
                      <td>SO_O</td>
                      <td>
                        <Form.Select
                          size="sm"
                          id="SoO"
                          name="SoO"
                          defaultValue={normalizeBoolToOption(
                            getDefaultValueForNamedField("SoO"),
                          )}
                          style={{
                            border: "1px solid #d1d5db",
                            borderRadius: "6px",
                            padding: "8px 12px",
                            fontSize: "16px",
                          }}
                        >
                          {boolOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                              {o.label}
                            </option>
                          ))}
                        </Form.Select>
                      </td>
                    </tr>
                  </>
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {error && (
          <div className="mt-3">
            <Alert variant="danger">
              {error?.message || "Nie udało się zapisać zmian."}
            </Alert>
          </div>
        )}
        {success && (
          <div className="mt-3">
            <Alert variant="success">{successMessage}</Alert>
          </div>
        )}
      </div>
    </Form>
  );
};

export default InformationUpdateForms;
